import { reactive } from "@vue/composition-api";
import {
  IlsctlLineItem,
  IlsctlLineItemApiParams,
  FormIlsctlLineItem,
  buildFormIlsctlLineItems,
} from "@/models/IlsctlLineItem";

import {
  TruckLoadLineItem,
  TruckLoadLineItemApiParams,
  FormTruckLoadLineItem,
  buildFormTruckLoadLineItems,
} from "@/models/TruckLoadLineItem";

import { buildFormTruckAutocomplete } from "@/models/Truck";
import { buildFormLotUnitAutocomplete } from "@/models/LotUnit";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";

export const enum TruckLoadStatus {
  pending = "pending",
  completed = "completed",
}

const prettifiedTruckLoadStatus = {
  pending: "Pending",
  completed: "Completed",
};

export const prettifyTruckLoadStatus = (s: TruckLoadStatus) => {
  return prettifiedTruckLoadStatus[s];
};

export const truckLoadStatusReverseLookup = <
  { [key: string]: TruckLoadStatus }
>{
  pending: TruckLoadStatus.pending,
  completed: TruckLoadStatus.completed,
};

export interface TruckLoadApiParams {
  id: string;
  truck_id: string;
  bill_of_lading: string;
  ticket: string;
  scheduled_date: string;
  completed_date: string;
  status: string;
  tare_weight: number;
  gross_weight: number;
  weight_lot_unit_id: string;
  ilsctl_line_items_attributes: IlsctlLineItemApiParams[];
  truck_load_line_items_attributes: TruckLoadLineItemApiParams[];
}

export interface TruckLoad {
  id: string;
  name: string;
  truck_id: string;
  truck_label: string;
  bill_of_lading: string;
  ticket: string;
  scheduled_date: string;
  completed_date: string;
  status: string;
  tare_weight: number;
  gross_weight: number;
  net_weight: number;
  weight_lot_unit_id: string;

  weight_lot_unit_label: string;

  ilsctl_line_items: IlsctlLineItem[];
  truck_load_line_items: TruckLoadLineItem[];
}

export interface FormTruckLoad extends TruckLoadApiParams {
  ilsctl_line_items_attributes: FormIlsctlLineItem[];
  truck_load_line_items_attributes: FormTruckLoadLineItem[];
  formWeightLotUnit: AutocompleteResult;
  formTruck: AutocompleteResult;
}

export function buildNewTruckLoad(): TruckLoad {
  return {
    id: "",
    name: "",
    truck_id: "",
    truck_label: "",
    bill_of_lading: "",
    ticket: "",
    scheduled_date: "",
    completed_date: "",
    status: "pending",
    tare_weight: 0,
    gross_weight: 0,
    net_weight: 0,
    weight_lot_unit_id: "",
    weight_lot_unit_label: "",
    ilsctl_line_items: [],
    truck_load_line_items: [],
  };
}

export function buildFormTruckLoad() {
  let { formTruck } = buildFormTruckAutocomplete();
  let { formLotUnit } = buildFormLotUnitAutocomplete();

  let { loadIlsctlLineItemsIntoForm } = buildFormIlsctlLineItems();

  let { loadTruckLoadLineItemsIntoForm } = buildFormTruckLoadLineItems();

  let formTruckLoad = reactive<FormTruckLoad>({
    id: "",
    truck_id: "",
    bill_of_lading: "",
    ticket: "",
    scheduled_date: "",
    completed_date: "",
    status: "pending",
    tare_weight: 0,
    gross_weight: 0,
    weight_lot_unit_id: "",
    formTruck: formTruck,
    formWeightLotUnit: formLotUnit(),
    ilsctl_line_items_attributes: [],
    truck_load_line_items_attributes: [],
  });

  let loadTruckLoadIntoForm = (tl: TruckLoad, ftl: FormTruckLoad) => {
    ftl.id = tl.id;
    ftl.truck_id = tl.truck_id;
    ftl.bill_of_lading = tl.bill_of_lading;
    ftl.ticket = tl.ticket;
    ftl.scheduled_date = tl.scheduled_date;
    ftl.completed_date = tl.completed_date;
    ftl.status = tl.status;
    ftl.tare_weight = tl.tare_weight;
    ftl.gross_weight = tl.gross_weight;
    ftl.weight_lot_unit_id = tl.weight_lot_unit_id;

    ftl.formWeightLotUnit.id = tl.weight_lot_unit_id;
    ftl.formWeightLotUnit.label = tl.weight_lot_unit_label;
    ftl.formTruck.id = tl.truck_id;
    ftl.formTruck.label = tl.truck_label;
  };

  let truckLoadStatuses = reactive<TruckLoadStatus[]>([]);

  const loadTruckLoadStatusesIntoForm = (tlStatuses: TruckLoadStatus[]) => {
    tlStatuses.push(TruckLoadStatus.pending, TruckLoadStatus.completed);
  };

  return {
    formTruckLoad,
    loadTruckLoadIntoForm,
    truckLoadStatuses,
    loadTruckLoadStatusesIntoForm,
  };
}
